/**
 * @file 公共用户状态管理
 * @description 统一管理用户信息、设备信息、定位信息等
 */

import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { Pinia } from 'pinia'

/** 会员基本信息 */
export interface MemberBasicInfo {
  /** 会员ID */
  memberId?: string
  /** 会员编码 */
  memberCode?: string
  /** 头像 */
  headerPic?: string
  /** 会员等级 */
  memberLevel?: string
  /** 注册时间 */
  registerTime?: string
  /** 积分 */
  Integral?: number
  /** 名 */
  firstName?: string
  /** 姓 */
  lastName?: string
  /** 姓氏 */
  surname?: string
  /** 姓名 */
  name?: string
  /** 称谓 */
  appellation?: string
  /** 性别 */
  sex?: number
  /** 国际区号 */
  internationalCode?: string
  /** 手机号 */
  mobile?: string
  /** 邮箱 */
  email?: string
  /** 出生日期 */
  birthDate?: string
  /** 会员类型名称 */
  memberTypeName?: string
  /** 公司标识 */
  companyFlag?: number
  /** 个人资料权限 */
  personalDataPermission?: number
  /** 禁止推广渠道 */
  forbiddenPromotionChannel?: string
  /** 推广语言 */
  promotionLanguage?: string
  /** 用户推广码 */
  userPromotionCode?: string
  /** 公司推广码 */
  companyPromotionCode?: string
  /** 公司ID */
  companyId?: number
  /** 公司名称 */
  companyName?: string
  /** 邮箱验证状态 */
  emailVerifyStatus?: number
}

/** 用户信息类型 */
export interface UserInfo {
  mobile?: string
  memberId?: string
  loginId?: string
  loginToken?: string
  name?: string
  copId?: string
  oneId?: string
  /** 会员基本信息 */
  memberBasicInfo?: MemberBasicInfo
  [key: string]: unknown
}

/** 设备信息类型 */
export interface DeviceInfo {
  platform?: string
  version?: string
  [key: string]: unknown
}

/** 定位信息类型 */
export interface LocationInfo {
  cityId?: string
  cityName?: string
  isAbroad?: boolean
  latitude?: number
  longitude?: number
  [key: string]: unknown
}

/** 存储类型枚举 */
export enum StorageType {
  SESSION = 'session',
  LOCAL = 'local'
}

/** 存储适配器 */
const storageAdapter = {
  [StorageType.SESSION]: sessionStorage,
  [StorageType.LOCAL]: localStorage
}

/** 用户数据存储配置 */
const USER_STORAGE_KEYS = ['mobile', 'loginToken', 'memberId', 'loginId', 'name', 'oneId'] as const
const USER_INFO_KEY = 'userInfo'

/** 定位信息存储 key 列表 */
const LOCATION_STORAGE_KEYS = ['cityId', 'cityName', 'isAbroad'] as const
const LOCATION_INFO_KEY = 'locationInfo'

/**
 * 统一存储操作
 */
const storage = {
  set(key: string, value: string, type: StorageType = StorageType.LOCAL): void {
    storageAdapter[type].setItem(key, value)
  },
  get(key: string, type: StorageType = StorageType.LOCAL): string | null {
    return storageAdapter[type].getItem(key)
  },
  remove(key: string, type: StorageType = StorageType.LOCAL): void {
    storageAdapter[type].removeItem(key)
  },
  /**
   * 批量存储字段
   */
  setMany(data: Record<string, string>, type: StorageType = StorageType.LOCAL): void {
    Object.entries(data).forEach(([key, value]) => {
      if (value) storageAdapter[type].setItem(key, value)
    })
  },
  /**
   * 批量清除字段
   */
  removeMany(keys: readonly string[], type: StorageType = StorageType.LOCAL): void {
    keys.forEach((key) => storageAdapter[type].removeItem(key))
  },
  clear(type: StorageType = StorageType.LOCAL): void {
    storageAdapter[type].clear()
  }
}

/**
 * 用户 Store
 */
export const useUserStore = defineStore('user', () => {
  // 用户核心字段
  const token = ref<string>('')
  const mobile = ref<string>('')
  const memberId = ref<string>('')
  const loginId = ref<string>('')
  const loginToken = ref<string>('')
  const name = ref<string>('')
  const oneId = ref<string>('')

  // 其他字段
  const roles = ref<string[]>([])
  const username = ref<string>('')
  const userInfo = ref<UserInfo | null>(null)
  const deviceInfo = ref<DeviceInfo | null>(null)
  const locationInfo = ref<LocationInfo | null>(null)
  const statusBarHeight = ref<number>(20)

  /**
   * 设置用户信息
   */
  const setUserInfo = (info: UserInfo): void => {
    userInfo.value = info
    const {
      mobile: _mobile = '',
      loginToken: _loginToken = '',
      memberId: _memberId = '',
      loginId: _loginId = '',
      name: _name = '',
      oneId: _oneId = ''
    } = info

    // 同步更新响应式变量
    mobile.value = _mobile
    memberId.value = _memberId
    loginId.value = _loginId
    loginToken.value = _loginToken
    token.value = _loginToken
    name.value = _name
    oneId.value = _oneId

    // 统一存储到 sessionStorage
    const userData = {
      mobile: _mobile,
      loginToken: _loginToken,
      memberId: _memberId,
      loginId: _loginId,
      name: _name,
      oneId: _oneId
    }
    storage.set(USER_INFO_KEY, JSON.stringify(userData), StorageType.LOCAL)
    storage.setMany(userData, StorageType.LOCAL)
  }

  /**
   * 设置设备信息
   */
  const setDeviceInfo = (info: DeviceInfo): void => {
    deviceInfo.value = info
  }

  /**
   * 设置 Token
   */
  const setToken = (value: string): void => {
    token.value = value
    storage.set('loginToken', value, StorageType.LOCAL)
  }

  /**
   * 重置 Token
   */
  const resetToken = (): void => {
    token.value = ''
    roles.value = []
    storage.remove('loginToken', StorageType.LOCAL)
  }

  /**
   * 设置状态栏高度
   */
  const setStatusBarHeight = (height: number): void => {
    statusBarHeight.value = height
  }

  /**
   * 设置定位信息
   */
  const setLocationInfo = (location: LocationInfo): void => {
    locationInfo.value = location
    const { cityId = '', cityName = '', isAbroad = false } = location
    const locationData = { cityId, cityName, isAbroad: String(isAbroad) }
    storage.set(LOCATION_INFO_KEY, JSON.stringify(locationData), StorageType.LOCAL)
    storage.setMany(locationData, StorageType.LOCAL)
  }

  /**
   * 清除用户信息
   */
  const clearUserInfo = (): void => {
    userInfo.value = null
    token.value = ''
    mobile.value = ''
    memberId.value = ''
    loginId.value = ''
    loginToken.value = ''
    name.value = ''
    oneId.value = ''
    roles.value = []
    storage.remove(USER_INFO_KEY, StorageType.LOCAL)
    storage.removeMany(USER_STORAGE_KEYS, StorageType.LOCAL)
    storage.removeMany(LOCATION_STORAGE_KEYS, StorageType.LOCAL)
  }

  /**
   * 从 storage 恢复用户信息
   */
  const restoreUserInfo = (): void => {
    const stored = storage.get(USER_INFO_KEY, StorageType.LOCAL)
    if (stored) {
      try {
        const info = JSON.parse(stored) as UserInfo
        userInfo.value = info
        mobile.value = info.mobile ?? ''
        memberId.value = info.memberId ?? ''
        loginId.value = info.loginId ?? ''
        loginToken.value = info.loginToken ?? ''
        token.value = info.loginToken ?? ''
        name.value = info.name ?? ''
        oneId.value = info.oneId ?? ''
      } catch {
        // ignore
      }
    } else {
      userInfo.value = {
        mobile: '',
        loginToken: '',
        memberId: '',
        loginId: '',
        name: '',
        oneId: ''
      }
      USER_STORAGE_KEYS.forEach((item) => {
        const itemValue = storage.get(item) as string
        switch (item) {
          case 'mobile':
            mobile.value = itemValue
            userInfo.value!.mobile = itemValue
            break
          case 'loginToken':
            loginToken.value = itemValue
            userInfo.value!.loginToken = itemValue
            break
          case 'memberId':
            memberId.value = itemValue
            userInfo.value!.memberId = itemValue
            break
          case 'loginId':
            loginId.value = itemValue
            userInfo.value!.loginId = itemValue
            break
          case 'name':
            name.value = itemValue
            userInfo.value!.name = itemValue
            break
          case 'oneId':
            oneId.value = itemValue
            userInfo.value!.oneId = itemValue
            break
        }
      })
    }
  }

  /**
   * 检查是否已登录
   */
  const hasLoginToken = (): boolean => {
    return !!token.value
  }

  /**
   * 将登录态同步到 localStorage（记住登录）
   */
  const syncToLocalStorage = (): void => {
    const userData = {
      mobile: mobile.value,
      loginToken: loginToken.value,
      memberId: memberId.value,
      loginId: loginId.value,
      name: name.value,
      oneId: oneId.value
    }
    storage.set(USER_INFO_KEY, JSON.stringify(userData), StorageType.LOCAL)
    storage.setMany(userData, StorageType.LOCAL)
  }

  /**
   * 清除 localStorage 中的记住登录态
   */
  const clearLocalStorage = (): void => {
    storage.remove(USER_INFO_KEY, StorageType.LOCAL)
    storage.removeMany(USER_STORAGE_KEYS, StorageType.LOCAL)
  }

  /**
   * 退出登录并清空浏览器本地缓存
   */
  const logout = (): void => {
    clearUserInfo()
    storage.clear(StorageType.LOCAL)
    storage.clear(StorageType.SESSION)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getItem = (key: string): any => {
    return storage.get(key)
  }

  return {
    token,
    mobile,
    memberId,
    loginId,
    loginToken,
    name,
    oneId,
    roles,
    username,
    userInfo,
    deviceInfo,
    statusBarHeight,
    locationInfo,
    setToken,
    resetToken,
    setUserInfo,
    setDeviceInfo,
    setStatusBarHeight,
    setLocationInfo,
    clearUserInfo,
    restoreUserInfo,
    hasLoginToken,
    syncToLocalStorage,
    clearLocalStorage,
    logout,
    getItem
  }
})

let globalPinia: Pinia | null = null

/**
 * 设置全局 Pinia 实例，供 setup 外使用 store
 */
export function setGlobalPinia(pinia: Pinia): void {
  globalPinia = pinia
}

/**
 * 在 setup 外使用 store（必须先在 main.ts 中调用 setGlobalPinia）
 */
export function useUserStoreOutside() {
  if (!globalPinia) {
    throw new Error(
      '[useUserStoreOutside] 全局 Pinia 实例未初始化，请在 app.use(pinia) 后调用 setGlobalPinia(pinia)'
    )
  }
  return useUserStore(globalPinia)
}
